import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as cts from '../../../../constants/ui/svg';
import { IMAGE_BASE_URL } from '../../../../constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { rankingAction } from 'slices/rankingSlice';
import { getToken } from 'helpers';
import { Modal, Tooltip } from 'antd';
import { useKeyboardShortcut } from 'hooks/useKeyboardShortcut';
import ModalDeleteImage from '../ModalDeleteImage';
import Cropper, { Area, Point } from 'react-easy-crop';
import moment from 'moment';
import { createImage, getCroppedImg } from 'helpers/functionHelper';
import { useDimension } from 'hooks/useDimension';

interface IStandByImagesProps {
  listImageWaitingScreen: Array<any>;
  setListImageWaitingScreen: React.Dispatch<React.SetStateAction<any>>;
}

const StandByImages: React.FC<IStandByImagesProps> = (props) => {
  const { listImageWaitingScreen, setListImageWaitingScreen } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const token = getToken();
  const { width } = useDimension();
  const isMobile = width < 768;

  const defaultCroppedArea: Area = {
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  };

  const imageMimeType = /(image)\/(png|jpg|jpeg|webp|gif)/i;

  const inputRef = useRef<any>();
  // https://stackoverflow.com/questions/35701326/how-to-stop-ondragleave-from-firing-when-moving-from-container-to-children-in-re
  // Because of firing onMouseLeave from children element to parent element, we will use this drag counter to handle it
  const dragCounter = useRef(0);

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [croppedArea, setCroppedArea] = useState<Area>(defaultCroppedArea);
  const [mediaFileEdit, setMediaFileEdit] = useState<string>('');
  const [openCropModal, setOpenCropModal] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showImageDetail, setShowImageDetail] = useState<boolean>(false);
  const [openDeleteMediaModal, setOpenDeleteMediaModal] = useState<boolean>(false);
  const [isDraggedInDropzone, setIsDraggedInDropzone] = useState<boolean>(false);
  const [renderCropper, setRenderCropper] = useState(false);
  const [zoomRatio, setZoomRatio] = useState<number>(1);

  const selectShop = useAppSelector((state) => state.shopList.selectShop);
  const listPermissionByUser = useAppSelector((state) => state.shopSetting.permissionByUser);

  const listShopHasPermission = listPermissionByUser
    .filter((item) => item.name == 'enable_ranking_program_master')
    .map((mapItem) => mapItem.shop_id);

  const goToNextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === listImageWaitingScreen.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToPreviousSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? listImageWaitingScreen.length - 1 : prevIndex - 1
    );
  };

  // common
  const mediaUrlLink = (url: string, typeImage: string) => {
    return `${IMAGE_BASE_URL}/${typeImage}/${url}`;
  };

  // funcs
  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  };
  const autoSave = (listImage: any) => {
    if (token) {
      dispatch(
        rankingAction.addStandByMedia({
          token,
          listMedia: listImage.map((item: any) => {
            return {
              imageFile: item.imageFile,
              order_by: item?.order_by,
              ...(selectShop?.id
                ? { shop_id: selectShop.id }
                : { shop_ids: listShopHasPermission }),
            };
          }),
        })
      );
    }
  };

  const processFiles = async (files: File[], disableAutoSave: boolean = false) => {
    if (!files.length) return;
    const filesArr = [...files];
    const validFiles = filesArr.filter((file) => file.type.match(imageMimeType));

    if (validFiles.length === 0) {
      alert('Video type is not valid');
      return;
    }

    // Convert each file to Promise reader
    const readFilePromises = validFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            file,
            result: reader.result,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    // Wait for all files to finish reading
    const results: any[] = await Promise.all(readFilePromises);

    const newImages = results.map(({ file, result }) => {
      const maxOrder = Math.max(
        ...listImageWaitingScreen.map((item: any) => item.order_by || 0),
        0
      );

      return {
        imageUrl: result,
        imageFile: file,
        isNew: true,
        order_by: maxOrder + 1,
        fileType: 'image',
      };
    });

    setListImageWaitingScreen((prev: any[]) => {
      const finalList = [...prev, ...newImages];
      if (!disableAutoSave) {
        autoSave(newImages);
      }
      return finalList;
    });
  };

  const handleToggleShowImageDetail = () => {
    setShowImageDetail(!showImageDetail);
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current += 1;

    if (dragCounter.current === 1) {
      setIsDraggedInDropzone(true);
    }
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current -= 1;

    if (dragCounter.current === 0) {
      setIsDraggedInDropzone(false);
    }
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDraggedInDropzone(false);
    const files = Array.from(e.dataTransfer.files);
    const previewUrl = URL.createObjectURL(files[0]);
    setMediaFileEdit(previewUrl);
    toggleOpenCropperModal();
    // await processFiles(files);
  };

  const handleCropImage = async () => {
    const blob: any = await getCroppedImg(mediaFileEdit, croppedArea);
    const file = new File([blob], `stand_by_${moment.now()}.jpg`, { type: 'image/jpeg' });

    toggleOpenCropperModal();
    // reset after crop
    setCroppedArea(defaultCroppedArea);
    setZoomRatio(1);
    processFiles([file]);
  };

  const handleDeleteImage = () => {
    if (token) {
      if (selectShop.id) {
        dispatch(
          rankingAction.deleteImageSlider({
            token,
            id: listImageWaitingScreen[currentIndex]?.id,
            data: { shop_id: selectShop?.id },
            onSuccess: () => {
              setListImageWaitingScreen((prevList: any) =>
                prevList.filter(
                  (item: any) => item?.id !== listImageWaitingScreen[currentIndex]?.id
                )
              );
            },
          })
        );
      } else {
        dispatch(
          rankingAction.deleteImageSlider({
            token,
            id: listImageWaitingScreen[currentIndex]?.id,
            data: { shop_ids: listShopHasPermission },
            onSuccess: () => {
              setListImageWaitingScreen((prevList: any) =>
                prevList.filter(
                  (item: any) => item?.id !== listImageWaitingScreen[currentIndex]?.id
                )
              );
            },
          })
        );
      }
      handleToggleShowImageDetail();
    }
    setOpenDeleteMediaModal(false);
  };

  // upload images
  const handleUploadStandbyImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    const previewUrl = URL.createObjectURL(files[0]);
    setMediaFileEdit(previewUrl);
    toggleOpenCropperModal();
    // processFiles(files, true);
    input.value = '';
  };

  // render
  const __renderStandByImage = (item: any, src: string, index: number) => {
    return (
      <Tooltip
        title={isMobile ? '' : String(src)}
        placement="top"
        overlayClassName="no-padding-bottom-tooltip"
      >
        <div className="lg:w-[98px] w-full lg:h-auto h-full">
          <div
            onClick={() => {
              handleToggleShowImageDetail();
              setCurrentIndex(index);
            }}
            className="relative rounded-lg cursor-pointer lg:size-[98px] w-full object-cover overflow-hidden aspect-square"
          >
            <img
              draggable={false}
              src={item.imageUrl || mediaUrlLink(src, 'images_slider')}
              alt=""
              className="object-cover w-full h-full pointer-events-none"
            />
          </div>

          <span className="w-full text-base break-all cursor-pointer line-clamp-2">{src}</span>
        </div>
      </Tooltip>
    );
  };

  const handleCheckHasArrData = (arrData: Array<any>) => {
    if (!arrData.length) return <></>;
    return arrData;
  };

  const toggleOpenCropperModal = () => {
    setOpenCropModal(!openCropModal);
  };

  const handleZoom = (type: 'zoom-in' | 'zoom-out') => {
    if (type == 'zoom-out') {
      if (zoomRatio > 1) {
        setZoomRatio((prev) => (prev -= 0.1));
      }
    } else {
      if (zoomRatio <= 3) {
        setZoomRatio((prev) => (prev += 0.1));
      }
    }
  };

  // fetch standby images
  useEffect(() => {
    if (token) {
      if (selectShop.id) {
        const params = {
          shop_id: selectShop?.id,
          type: 1,
        };
        dispatch(rankingAction.getImageSlider({ token, params }));
      } else {
        const params = {
          permission: 'master',
          type: 1,
        };
        dispatch(rankingAction.getImageSlider({ token, params }));
      }
    }
  }, [selectShop.id, token]);

  useKeyboardShortcut({
    key: 'ArrowRight',
    onKeyPressed: goToNextSlide,
  });
  useKeyboardShortcut({
    key: 'ArrowLeft',
    onKeyPressed: goToPreviousSlide,
  });

  useEffect(() => {
    if (openCropModal) {
      setTimeout(() => setRenderCropper(true), 300);
    } else {
      setRenderCropper(false);
    }
  }, [openCropModal]);
  return (
    <div
      className="relative px-4 py-3 h-full"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      {/* Standby images header */}
      <div className="flex justify-between items-center">
        <span className="text-xl font-notosans-700">{t('STILL_PHOTO')}</span>
        <button
          onClick={() => inputRef.current?.click()}
          className="flex gap-2 items-center px-4 h-11 text-base text-white rounded-lg font-notosans-400 bg-primary"
        >
          <span>{cts.add}</span>
          <span>{t('UPLOAD')}</span>
        </button>
      </div>
      <input
        id="inputAvatar"
        ref={inputRef}
        accept=".png, .jpg, .jpeg, .webp, .gif,"
        type="file"
        onChange={handleUploadStandbyImage}
        className="hidden"
        // multiple
      />
      {isDraggedInDropzone && (
        <div className="flex absolute inset-0 z-50 justify-center items-center pointer-events-none bg-black/40">
          <div className="text-2xl font-semibold text-white">{t('PLEASE_UPLOAD_THE_IMAGE')}</div>
        </div>
      )}

      {/* Standby images body*/}
      {/* PC */}
      <div className="lg:flex hidden flex-wrap max-h-[calc(100vh-244px)] h-fit gap-4 mt-3 overflow-auto ">
        {handleCheckHasArrData(listImageWaitingScreen) &&
          listImageWaitingScreen
            .filter(
              (item: any) =>
                item?.link?.includes('jpg') ||
                item?.link?.includes('png') ||
                item?.link?.includes('jpeg') ||
                item?.link?.includes('webp') ||
                item?.fileType === 'image'
            )
            .map((item, index) => (
              <React.Fragment key={item.id}>
                {__renderStandByImage(item, item.link, index)}
              </React.Fragment>
            ))}
      </div>
      {/* Mobile */}
      <div className="lg:hidden grid grid-cols-2 max-h-[calc(100vh-244px)] h-fit gap-4 mt-3 overflow-auto">
        {handleCheckHasArrData(listImageWaitingScreen) &&
          listImageWaitingScreen
            .filter(
              (item: any) =>
                item?.link?.includes('jpg') ||
                item?.link?.includes('png') ||
                item?.link?.includes('jpeg') ||
                item?.link?.includes('webp') ||
                item?.fileType === 'image'
            )
            .map((item, index) => (
              <React.Fragment key={item.id}>
                {__renderStandByImage(item, item.link, index)}
              </React.Fragment>
            ))}
      </div>
      {/* Modal select image detail */}
      <Modal
        open={showImageDetail}
        onOk={handleToggleShowImageDetail}
        onCancel={handleToggleShowImageDetail}
        title={null}
        footer={null}
        closeIcon={<></>}
        width={'fit-content'}
        className="override-antd-modal-border-radius"
        bodyStyle={{
          padding: 0,
        }}
        centered
      >
        {/* Modal header */}
        <div className="flex justify-between items-center px-4 py-2 bg-white rounded-t-lg border-b border-border-gray">
          <p className="text-xl font-notosans-700 text-gray_800">{t('STILL_PHOTO')}</p>
          <button className="text-base text-primary font-notosans-400">{t('CLOSE')}</button>
        </div>

        {/* Modal body */}
        <div className="relative size-[450px] border-none bg-white !rounded-b-lg">
          <img
            className="object-contain size-full"
            src={
              listImageWaitingScreen[currentIndex]?.link
                ? mediaUrlLink(listImageWaitingScreen[currentIndex]?.link, 'images_slider')
                : listImageWaitingScreen[currentIndex]?.imageUrl
            }
            alt=""
          />
          <button
            onClick={goToPreviousSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full size-10 bg-[#333333CC]"
          >
            {cts.sliderPrevArrowIcon}
          </button>
          <button
            onClick={goToNextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full size-10 bg-[#333333CC]"
          >
            {cts.sliderNextArrowIcon}
          </button>
        </div>
        <div className="p-3 w-full text-sm text-center truncate font-notosans-300">
          {listImageWaitingScreen[currentIndex]?.link}
        </div>
        {/* Modal footer */}
        <div className="flex gap-2 items-center p-2 bg-white rounded-b-lg border-t border-border-gray">
          <button
            onClick={handleToggleShowImageDetail}
            className="w-1/2 h-9 text-base rounded-lg border border-solid border-border-gray text-primary font-notosans-400"
          >
            {t('CANCEL')}
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpenDeleteMediaModal(true);
            }}
            className="w-1/2 text-base h-9 border border-solid border-[#F04438] font-notosans-400 text-[#F04438] flex items-center gap-2 rounded-lg justify-center"
          >
            {cts.trashSimpleIcon}
            {t('DELETE')}
          </button>
        </div>
      </Modal>
      <ModalDeleteImage
        openConfirm={openDeleteMediaModal}
        setOpenConfirm={setOpenDeleteMediaModal}
        handleDeleteImage={handleDeleteImage}
        title={t('CONFIRM_DEL_IMG')}
        isInProgram={Boolean(
          listImageWaitingScreen[currentIndex]?.order_by_images_slider_ranking[0]?.id
        )}
      />
      <Modal
        centered
        open={openCropModal}
        destroyOnClose
        onOk={toggleOpenCropperModal}
        onCancel={toggleOpenCropperModal}
        width={600}
        bodyStyle={{
          padding: 0,
        }}
        title={null}
        footer={null}
        closeIcon={<></>}
        className="override-antd-modal-border-radius"
      >
        <div className="flex justify-between items-center px-4 py-2 bg-white rounded-t-lg border-b border-border-gray">
          <p className="text-xl font-notosans-700 text-gray_800">写真</p>
          <button className="text-base text-primary font-notosans-400">{t('CLOSE')}</button>
        </div>
        <div className="flex relative flex-col">
          <div className="flex flex-col">
            <div className="relative w-full h-[380px]">
              {renderCropper && (
                <Cropper
                  image={mediaFileEdit}
                  crop={crop}
                  onCropChange={setCrop}
                  zoom={zoomRatio}
                  onZoomChange={setZoomRatio}
                  restrictPosition={false}
                  onCropComplete={onCropComplete}
                  aspect={1}
                  cropShape="rect"
                  showGrid={false}
                  objectFit={'contain'}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                />
              )}
            </div>
          </div>
          <div className="flex gap-2 items-center mx-auto w-fit">
            <button onClick={() => handleZoom('zoom-out')}>{cts.zoomOut}</button>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoomRatio}
              className="py-3 w-[200px] accent-primary mx-auto"
              onChange={(e) => setZoomRatio(Number(e.target.value))}
            />
            <button onClick={() => handleZoom('zoom-in')}>{cts.zoomIn}</button>
          </div>
        </div>
        {/* Modal footer */}
        <div className="flex gap-2 items-center p-2 bg-white rounded-b-lg border-t border-border-gray">
          <button
            onClick={toggleOpenCropperModal}
            className="w-1/2 h-9 text-base rounded-lg border border-solid border-border-gray text-primary font-notosans-400"
          >
            {t('CANCEL')}
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCropImage();
            }}
            className="w-1/2 h-9 text-base text-white rounded-lg bg-primary font-notosans-400"
          >
            {t('KEEP')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default memo(StandByImages);
